import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ListRenderItem } from '@shopify/flash-list';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import { difference, isEqual } from 'lodash';
import React from 'react';
import { API_ASE_IMAGE } from 'service/api/constants';
import {
  selectAseTplFieldGroup,
  selectAseFieldInstanceIdsForGroupFilteredAndSorted,
  selectAseId,
  selectAseIsOnline,
  selectAseFieldInstanceChildInstanceIdsAst,
  selectAseIsEditable,
  selectAseFieldInstanceChildInstanceIdsAstAse,
} from 'store/assessments/selectors';
import { selectUrlToken } from 'store/auth/selectors';
import { arrayUnique } from 'lib/riskbase';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { eventTypeScrollToItem } from 'store/actions';
import { InstanceParentParams } from 'store/assessments/types';
import { eventKeyAseScrollToItemInstanceExplorer } from 'types/types';
import { RepeatableListEmpty } from './RepeatableListEmpty';
import { RepeatableListFilter } from './RepeatableListFilter';
import { RepeatableListItem } from './RepeatableListItem';
import { RepeatableListScreenHeader } from './RepeatableListScreenHeader';
import { QrIndicator } from '../QrIndicator';
import { List, ListRef } from 'components/List';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASEREPEATABLE],
  Screens.ASEREPEATABLE_LIST
>;

const Screen: React.FC<{
  navigation: Props['navigation'];
  groupId: string;
  parent: InstanceParentParams;
}> = ({ navigation, groupId, parent }) => {
  const urlToken = useSelector(selectUrlToken);
  const assessmentId = useSelector(selectAseId);
  const isEditable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);

  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));

  const instanceIds = useSelector((state) =>
    selectAseFieldInstanceIdsForGroupFilteredAndSorted(state, {
      groupId,
      parent,
      inAssessmentOnly: !isEditable || isOnline || !parent,
    }),
  );

  // When viewing in the context of a parent field - get the field value from the asset
  const assetChildInstanceIds = useSelector(
    (state) =>
      selectAseFieldInstanceChildInstanceIdsAst(state, {
        fieldId: parent?.fieldId || '-1',
        instanceId: parent?.instanceId || null,
      }),
    isEqual,
  );
  const aseChildInstanceIds = useSelector(
    (state) =>
      selectAseFieldInstanceChildInstanceIdsAstAse(state, {
        fieldId: parent?.fieldId || '-1',
        instanceId: parent?.instanceId || null,
      }),
    isEqual,
  );

  const finalInstanceIds = React.useMemo(
    () =>
      parent
        ? arrayUnique([
            ...instanceIds,
            ...(isEditable
              ? difference(instanceIds, assetChildInstanceIds || [])
              : difference(
                  instanceIds,
                  assetChildInstanceIds || [],
                  aseChildInstanceIds || [],
                )),
          ])
        : instanceIds,
    [
      aseChildInstanceIds,
      assetChildInstanceIds,
      instanceIds,
      isEditable,
      parent,
    ],
  );

  const renderItem: ListRenderItem<string> = React.useCallback(
    ({ item }) => (
      <RepeatableListItem
        instanceId={item}
        from="list"
        remoteImageUrl={
          isOnline ? API_ASE_IMAGE(assessmentId, { urlToken }) : undefined
        }
        parent={parent}
      />
    ),
    [assessmentId, isOnline, urlToken, parent],
  );

  const listRef = React.useRef<ListRef<string>>(null);

  const eventKeyScrollToItem = eventKeyAseScrollToItemInstanceExplorer({
    assessmentId,
    groupId: group?.id || '',
  });

  const [headerHeight, setHeaderHeight] = React.useState<number>(0);
  const handleHeaderHeightChange = React.useCallback(
    (newHeaderHeight: number) => setHeaderHeight(newHeaderHeight),
    [],
  );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventTypeScrollToItem, (event) => {
        if (event.payload.key === eventKeyScrollToItem) {
          listRef.current?.scrollToItem({
            item: event.payload.item,
            animated: event.payload.animated,
            viewOffset: headerHeight + theme.padding,
          });
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [eventKeyScrollToItem, headerHeight, listRef]);

  if (!group) {
    return null;
  }

  return (
    <>
      {!parent && <QrIndicator />}
      <Page
        header={
          <>
            <RepeatableListScreenHeader
              navigation={navigation}
              groupId={groupId}
              parent={parent}
            />
            <RepeatableListFilter groupId={groupId} parent={parent} />
          </>
        }
        onHeaderChangeHeight={handleHeaderHeightChange}
      >
        {finalInstanceIds.length ? (
          <List
            listRef={listRef}
            data={finalInstanceIds}
            keyExtractor={(instanceId) => instanceId}
            renderItem={renderItem}
          />
        ) : (
          <RepeatableListEmpty groupId={groupId} parent={parent} />
        )}
      </Page>
    </>
  );
};

export const RepeatableListScreen: React.FC<Props> = ({
  navigation,
  route: {
    params: { groupId, parent },
  },
}) =>
  React.useMemo(
    () => <Screen navigation={navigation} groupId={groupId} parent={parent} />,
    [navigation, groupId, parent],
  );
